import { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  extractSections,
  getLevelNumber,
  handleDomChange,
  truncateByWords,
} from "../Utility/Leftpanel";

const LeftPanel = ({ dom, content, editor }) => {
  const [gData, setGData] = useState([]);
  const [draggedGroupKeys, setDraggedGroupKeys] = useState([]);

  // Handle fetching tree data from the DOM
  const handleGetTreeData = (dom) => {
    const sideContent = [];

    const pushIfExists = (element, title, level) => {
      if (element) {
        const key = element.getAttribute("key") || "";
        sideContent.push({
          key,
          title,
          level: `level${level}`,
          movable: false,
        });
      }
    };

    const front = dom?.querySelector("front");
    const main = dom?.querySelector("body1");
    const back = dom?.querySelector("back");
    const ack = back?.querySelector("ack");
    pushIfExists(front?.querySelector("article-title"), "Article Title", 1);
    pushIfExists(front?.querySelector("contrib-group"), "Authors", 2);
    if (main) {
      sideContent.push(...extractSections(main));
    }
    if (ack) {
      const p1 = ack.querySelector("p");
      const key = p1.getAttribute("key") || "";
      const titleText = "Acknowledgement";
      sideContent.push({
        key,
        title: titleText,
        level: "level2",
        movable: false,
      });
    }
    const fnGroup = back?.querySelector("fn-group");
    if (fnGroup) {
      const backTags = fnGroup.querySelectorAll("fn");
      backTags.forEach((fn) => {
        const key = fn.getAttribute("key") || "";
        const title1 = fn.querySelector("bold");
        const titleText = title1?.textContent.trim() || "";

        sideContent.push({
          key,
          title: titleText,
          level: "level2",
          movable: false,
        });
      });
    }

    const refTitle = back?.querySelector("ref-list > title1");
    if (refTitle) {
      const key = refTitle.getAttribute("key") || "";
      const titleText = refTitle.textContent.trim();
      sideContent.push({
        key,
        title: titleText,
        level: "level2",
        movable: false,
      });
    }

    setGData(sideContent);
  };

  // Scroll to the clicked element in the DOM
  const handleClick = (e, key) => {
    const editorbody = dom;

    if (key) {
      const targetElement = editorbody?.querySelector(`[key="${key}"]`);

      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  // Drag and Drop Functionalities
  const getGroup = (startIndex) => {
    const draggedItem = gData[startIndex];
    if (!draggedItem) return [];

    const group = [draggedItem.key.toString()];
    for (let i = startIndex + 1; i < gData.length; i++) {
      if (getLevelNumber(gData[i].level) > getLevelNumber(draggedItem.level)) {
        group.push(gData[i].key.toString());
      } else {
        break;
      }
    }
    return group;
  };

  const handleDragStart = (start) => {
    const group = getGroup(start.source.index);
    setDraggedGroupKeys(group);
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const dKey = gData[destination.index].key;
    const sKey = gData[source.index].key;
    const dlevel = gData[destination.index].level;
    const slevel = gData[source.index].level;
    const position = destination.index - source.index;

    if (draggedGroupKeys.length > 1) {
      const items = Array.from(gData);
      const groupItems = items.filter((i) =>
        draggedGroupKeys.includes(i.key.toString())
      );
      const remainingItems = items.filter(
        (i) => !draggedGroupKeys.includes(i.key.toString())
      );

      const destIndex = remainingItems.findIndex((item) => item.key === dKey);

      if (position > 0) {
        remainingItems.splice(destIndex + 1, 0, ...groupItems);
        setGData(remainingItems);
      } else if (position < 0) {
        remainingItems.splice(destIndex, 0, ...groupItems);
        setGData(remainingItems);
      }
    } else {
      const items = Array.from(gData);
      const [removed] = items.splice(source.index, 1);
      items.splice(destination.index, 0, removed);
      setGData(items);
    }
    handleDomChange(
      { key: sKey, level: slevel },
      { key: dKey, level: dlevel },
      position,
      dom,
      editor
    );
    setDraggedGroupKeys([]);
  };

  useEffect(() => {
    if (dom) {
      handleGetTreeData(dom);
    }
  }, [dom, content]);

  useEffect(() => {
    if (!editor) return;

    const handler = () => {
      if (dom) {
        handleGetTreeData(dom);
      }
    };

    editor.on("change", handler);

    return () => {
      editor.off("change", handler);
    };
  }, [editor, dom]);

  return (
    <div className="flex flex-col h-full bg-[#FFFFFF] w-full" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <div className="flex-1 overflow-y-auto leftpanel-content gap-2 pr-1">
        {gData.length ? (
          <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
            <Droppable droppableId="leftPanel">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="flex flex-col gap-1.5"
                >
                  {gData.map((item, index) => {
                    const isPartOfDraggedGroup = draggedGroupKeys.includes(item.key);

                    return (
                      <Draggable
                        key={item.key}
                        draggableId={item.key.toString()}
                        index={index}
                        isDragDisabled={!item.movable}
                      >
                        {(provided, snapshot) => {
                          if (snapshot.isDragging && draggedGroupKeys[0] === item.key) {
                            return (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                {draggedGroupKeys.map((gKey) => {
                                  const gItem = gData.find((i) => i.key === gKey);
                                  return (
                                    <p
                                      key={gKey}
                                      onClick={(e) => handleClick(e, gKey)}
                                      className={`${gItem.level} cursor-pointer text-[14px] px-2 py-1.5 relative flex items-center gap-2 rounded-[4px] text-[#44444C] hover:bg-[#F0EEEA] hover:text-[#17171A]`}
                                    >
                                      {gItem.movable && (
                                        <span className="mt-[2px] text-[#A8A69F]">
                                          <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="currentColor">
                                            <path d="M300 276.5a56 56 0 1056-97 56 56 0 00-56 97zm0 284a56 56 0 1056-97 56 56 0 00-56 97zM640 228a56 56 0 10112 0 56 56 0 00-112 0zm0 284a56 56 0 10112 0 56 56 0 00-112 0zM300 844.5a56 56 0 1056-97 56 56 0 00-56 97zM640 796a56 56 0 10112 0 56 56 0 00-112 0z" />
                                          </svg>
                                        </span>
                                      )}
                                      <span>{truncateByWords(gItem.title)}</span>
                                    </p>
                                  );
                                })}
                              </div>
                            );
                          }

                          const displayItem = (snapshot.isDragging || isPartOfDraggedGroup) ? (
                            <p
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{ opacity: 0.2 }}
                              className={`${item.level} cursor-pointer text-[14px] px-2 py-1.5 relative flex items-center gap-2 rounded-[4px] text-[#44444C] hover:bg-[#F0EEEA] hover:text-[#17171A]`}
                            >
                              {item.movable && (
                                <span className="mt-[2px] text-[#A8A69F]">
                                  <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="currentColor">
                                    <path d="M300 276.5a56 56 0 1056-97 56 56 0 00-56 97zm0 284a56 56 0 1056-97 56 56 0 00-56 97zM640 228a56 56 0 10112 0 56 56 0 00-112 0zm0 284a56 56 0 10112 0 56 56 0 00-112 0zM300 844.5a56 56 0 1056-97 56 56 0 00-56 97zM640 796a56 56 0 10112 0 56 56 0 00-112 0z" />
                                  </svg>
                                </span>
                              )}
                              <span>{truncateByWords(item.title)}</span>
                            </p>
                          ) : (
                            <p
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...(item.movable ? provided.dragHandleProps : {})}
                              onClick={(e) => handleClick(e, item.key)}
                              className={`${item.level} cursor-pointer text-[14px] px-2 py-1.5 relative flex items-center gap-2 rounded-[4px] text-[#44444C] hover:bg-[#F0EEEA] hover:text-[#17171A]`}
                            >
                              {item.movable && (
                                <span className="mt-[2px] text-[#A8A69F]" {...(item.movable ? provided.dragHandleProps : {})}>
                                  <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="currentColor">
                                    <path d="M300 276.5a56 56 0 1056-97 56 56 0 00-56 97zm0 284a56 56 0 1056-97 56 56 0 00-56 97zM640 228a56 56 0 10112 0 56 56 0 00-112 0zm0 284a56 56 0 10112 0 56 56 0 00-112 0zM300 844.5a56 56 0 1056-97 56 56 0 00-56 97zM640 796a56 56 0 10112 0 56 56 0 00-112 0z" />
                                  </svg>
                                </span>
                              )}
                              <span>{truncateByWords(item.title)}</span>
                            </p>
                          );

                          return displayItem;
                        }}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <div className="text-center text-xs text-[#9A988F] py-8">
            No sections found in this document.
          </div>
        )}
      </div>
    </div>
  );
};

export default LeftPanel;
